import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, MagnifyingGlassIcon, UserPlusIcon, UsersIcon, CheckIcon } from '@heroicons/react/24/outline'

const getInitials = (nome) => {
    if (!nome) return '?'
    return nome.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
}

const ModalVincularInvestidor = ({ states, setters, actionCreate, onSearchInvestidor, investidoresDisponiveis = [], loadingSearch }) => {
    const [searchTerm,       setSearchTerm]       = useState('')
    const [selectedInvestidor, setSelectedInvestidor] = useState(null)
    const [error, setError] = useState(null)

    const handleSearch = () => {
        if (!searchTerm.trim()) return
        onSearchInvestidor?.(searchTerm)
    }

    const handleSubmit = () => {
        if (!selectedInvestidor) { setError('Selecione um investidor'); return }
        actionCreate({
            empreendimento_id: states.InvestidoresInvestimentos?.empreendimento_id,
            investidor_id: selectedInvestidor.id,
            investidor_nome: selectedInvestidor.nome,
        })
    }

    const handleClose = () => {
        setSearchTerm(''); setSelectedInvestidor(null); setError(null)
        setters.setModalInvestidoresInvestimentos(false)
    }

    return (
        <Transition.Root show={states.modalInvestidoresInvestimentos} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all">

                                {/* Header */}
                                <div className="relative bg-gray-900 px-6 pt-6 pb-14 overflow-hidden">
                                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-brand-yellow/10" />
                                    <div className="absolute -bottom-12 -left-6 w-32 h-32 rounded-full bg-brand-yellow/5" />
                                    <div className="relative flex items-start justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-2xl bg-brand-yellow flex items-center justify-center flex-shrink-0 shadow-lg">
                                                <UserPlusIcon className="w-7 h-7 text-gray-900" />
                                            </div>
                                            <div>
                                                <span className="inline-block mb-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-green-400/20 text-green-300">Vincular</span>
                                                <Dialog.Title as="h3" className="text-xl font-bold text-white">Vincular Investidor</Dialog.Title>
                                                <p className="text-sm text-gray-400 mt-0.5">Busque e selecione um investidor</p>
                                            </div>
                                        </div>
                                        <button onClick={handleClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors">
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Body */}
                                <div className="relative -mt-6 mx-6 z-10">
                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 space-y-4">

                                        {/* Busca */}
                                        <div>
                                            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                                                <MagnifyingGlassIcon className="w-3.5 h-3.5" />Buscar Investidor
                                            </label>
                                            <div className="flex gap-2 mt-1.5">
                                                <input
                                                    type="text"
                                                    value={searchTerm}
                                                    onChange={e => setSearchTerm(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                                    placeholder="Nome ou e-mail..."
                                                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-brand-yellow transition-all"
                                                />
                                                <button
                                                    onClick={handleSearch}
                                                    disabled={loadingSearch}
                                                    className="px-4 py-2.5 rounded-xl bg-gray-900 text-brand-yellow text-sm font-semibold hover:opacity-90 transition-all disabled:opacity-50"
                                                >
                                                    {loadingSearch
                                                        ? <span className="h-4 w-4 rounded-full border-2 border-brand-yellow/30 border-t-brand-yellow animate-spin inline-block" />
                                                        : <MagnifyingGlassIcon className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Resultados */}
                                        {investidoresDisponiveis.length > 0 && (
                                            <div>
                                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                                                    {searchTerm.trim() ? 'Resultados da busca' : 'Investidores mais recentes'}
                                                </p>
                                                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                                                    {investidoresDisponiveis.map((inv) => (
                                                        <button
                                                            key={inv.id}
                                                            type="button"
                                                            onClick={() => { setSelectedInvestidor(inv); setError(null) }}
                                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left cursor-pointer transition-all ${
                                                                selectedInvestidor?.id === inv.id
                                                                    ? 'border-brand-yellow bg-brand-yellow/10'
                                                                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            <div className="pointer-events-none w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center text-brand-yellow text-xs font-bold flex-shrink-0">
                                                                {getInitials(inv.nome)}
                                                            </div>
                                                            <div className="pointer-events-none min-w-0 flex-1">
                                                                <p className="text-sm font-semibold text-gray-900 truncate">{inv.nome}</p>
                                                                <p className="text-xs text-gray-400 truncate">{inv.email}</p>
                                                            </div>
                                                            {selectedInvestidor?.id === inv.id && (
                                                                <CheckIcon className="pointer-events-none w-4 h-4 text-brand-yellow flex-shrink-0" />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {investidoresDisponiveis.length === 0 && searchTerm && !loadingSearch && (
                                            <div className="flex flex-col items-center py-6 text-center">
                                                <UsersIcon className="w-8 h-8 text-gray-300 mb-2" />
                                                <p className="text-sm text-gray-400">Nenhum investidor encontrado</p>
                                            </div>
                                        )}

                                        {error && <p className="text-xs text-red-500 flex items-center gap-1"><span>⚠</span>{error}</p>}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-end gap-3 px-6 pt-4 pb-6">
                                    <button type="button" onClick={handleClose} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all">Cancelar</button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={!selectedInvestidor}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark shadow-md hover:shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                    >
                                        <UserPlusIcon className="w-4 h-4" /> Vincular
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalVincularInvestidor
